import React from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Stack,
  keyframes,
} from "@mui/material";
import CustomAppBar from "components/CustomHeader";
import Footer from "components/Footer";

// Background gaming-style animations
const floatingParticles = keyframes`
  0% {
    transform: translateY(100vh) rotate(0deg);
    opacity: 0;
  }
  10% {
    opacity: 1;
  }
  90% {
    opacity: 1;
  }
  100% {
    transform: translateY(-100vh) rotate(360deg);
    opacity: 0;
  }
`;

const glowPulse = keyframes`
  0%, 100% {
    box-shadow: 0 0 20px rgba(79, 70, 229, 0.3);
  }
  50% {
    box-shadow: 0 0 40px rgba(79, 70, 229, 0.8);
  }
`;

const waveAnimation = keyframes`
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(100%);
  }
`;

const orbitAnimation = keyframes`
  0% {
    transform: rotate(0deg) translateX(100px) rotate(0deg);
  }
  100% {
    transform: rotate(360deg) translateX(100px) rotate(-360deg);
  }
`;

// Bingo-related animations
const bounceAnimation = keyframes`
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-10px);
  }
  60% {
    transform: translateY(-5px);
  }
`;

const pulseAnimation = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(79, 70, 229, 0.7);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 0 0 10px rgba(79, 70, 229, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(79, 70, 229, 0);
  }
`;

const slideInAnimation = keyframes`
  0% {
    transform: translateX(-100px);
    opacity: 0;
  }
  100% {
    transform: translateX(0);
    opacity: 1;
  }
`;

const rotateAnimation = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const fadeInUpAnimation = keyframes`
  0% {
    transform: translateY(30px);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
`;

// Background Particles Component
const BackgroundParticles = () => (
  <Box
    sx={{
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      pointerEvents: "none",
      zIndex: 0,
      overflow: "hidden",
    }}
  >
    {/* Floating Particles */}
    {Array.from({ length: 15 }).map((_, i) => (
      <Box
        key={i}
        sx={{
          position: "absolute",
          left: `${Math.random() * 100}%`,
          width: `${Math.random() * 6 + 2}px`,
          height: `${Math.random() * 6 + 2}px`,
          backgroundColor: [
            "#4F46E5",
            "#059669",
            "#DC2626",
            "#F59E0B",
            "#8B5CF6",
            "#EC4899",
          ][Math.floor(Math.random() * 6)],
          borderRadius: "50%",
          animation: `${floatingParticles} ${
            Math.random() * 10 + 10
          }s linear infinite`,
          animationDelay: `${Math.random() * 5}s`,
          opacity: 0.6,
        }}
      />
    ))}

    {/* Orbiting Elements */}
    {Array.from({ length: 8 }).map((_, i) => (
      <Box
        key={`orbit-${i}`}
        sx={{
          position: "absolute",
          top: `${Math.random() * 100}%`,
          left: `${Math.random() * 100}%`,
          width: "4px",
          height: "4px",
          backgroundColor: "rgba(255, 255, 255, 0.3)",
          borderRadius: "50%",
          animation: `${orbitAnimation} ${
            Math.random() * 20 + 15
          }s linear infinite`,
          animationDelay: `${Math.random() * 5}s`,
        }}
      />
    ))}

    {/* Glowing Waves */}
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "200%",
        height: "100%",
        background:
          "linear-gradient(90deg, transparent, rgba(79, 70, 229, 0.1), transparent)",
        animation: `${waveAnimation} 8s ease-in-out infinite`,
      }}
    />
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "200%",
        height: "100%",
        background:
          "linear-gradient(90deg, transparent, rgba(220, 38, 38, 0.1), transparent)",
        animation: `${waveAnimation} 12s ease-in-out infinite reverse`,
      }}
    />
  </Box>
);

export default function HowItWorks() {
  const features = [
    {
      icon: "🔗",
      title: "No Apps. No Downloads.",
      description: "Just a link and a card. Everyone can join in seconds.",
      color: "#4F46E5",
      bgColor: "#EEF2FF",
    },
    {
      icon: "🎨",
      title: "100% Customizable",
      description: "Add your themes: onboarding, Women's Day, milestones, etc.",
      color: "#059669",
      bgColor: "#ECFDF5",
    },
    {
      icon: "🌐",
      title: "Play Anywhere",
      description:
        "Office party, all-hands Zoom, town halls — we've done it all.",
      color: "#DC2626",
      bgColor: "#FEF2F2",
    },
  ];

  const horizontalScrollAnimation = keyframes`
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
`;

  const useCases = [
    { label: "Onboarding", color: "#4F46E5" },
    { label: "Women's Day", color: "#EC4899" },
    { label: "Milestones", color: "#F59E0B" },
    { label: "Team Building", color: "#10B981" },
    { label: "All-Hands", color: "#8B5CF6" },
    { label: "Town Halls", color: "#EF4444" },
  ];

  const venues = [
    { icon: "🏢", label: "Office Parties" },
    { icon: "📹", label: "Zoom Meetings" },
    { icon: "👥", label: "Town Halls" },
    { icon: "📅", label: "Corporate Events" },
  ];

  return (
    <Box sx={{ position: "relative", overflow: "hidden" }}>
      <CustomAppBar />

      {/* Background Animations */}
      <BackgroundParticles />

      <Box
        sx={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
        }}
      >
        {/* Section 1: Hero + Main Features */}
        <Box
          sx={{
            position: "relative",
            minHeight: "100vh",
            // background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            pt: 20,
            pb: 8,
            zIndex: 1,
          }}
        >
          <Container maxWidth="lg">
            {/* Header Section */}
            <Box sx={{ textAlign: "center", mb: 8 }}>
              <Typography
                variant="h2"
                component="h1"
                sx={{
                  fontWeight: "bold",
                  color: "white",
                  mb: 2,
                  fontSize: { xs: "2.5rem", md: "3.5rem" },
                  textShadow: "0 4px 8px rgba(0,0,0,0.3)",
                }}
              >
                Bingo That's Built for Business
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: "rgba(255, 255, 255, 0.9)",
                  maxWidth: 600,
                  mx: "auto",
                  lineHeight: 1.6,
                }}
              >
                Transform your corporate events with interactive bingo that
                everyone loves
              </Typography>
            </Box>

            {/* Main Features Grid */}
            <Grid container spacing={4}>
              {features.map((feature, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Card
                    sx={{
                      height: "100%",
                      background: "rgba(255, 255, 255, 0.95)",
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(255, 255, 255, 0.2)",
                      borderRadius: 3,
                      transition: "all 0.3s ease",
                      animation: `${glowPulse} 4s ease-in-out infinite`,
                      animationDelay: `${index * 0.5}s`,
                      "&:hover": {
                        transform: "translateY(-8px) scale(1.02)",
                        boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
                      },
                    }}
                  >
                    <CardContent sx={{ p: 4, textAlign: "center" }}>
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 80,
                          height: 80,
                          borderRadius: "50%",
                          backgroundColor: feature.bgColor,
                          color: feature.color,
                          mb: 3,
                          fontSize: "2.5rem",
                          animation: `${bounceAnimation} 2s infinite`,
                          animationDelay: `${index * 0.3}s`,
                        }}
                      >
                        {feature.icon}
                      </Box>
                      <Typography
                        variant="h5"
                        component="h3"
                        sx={{
                          fontWeight: "bold",
                          mb: 2,
                          color: "#1F2937",
                        }}
                      >
                        {feature.title}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: "#6B7280",
                          lineHeight: 1.6,
                          fontSize: "1.1rem",
                        }}
                      >
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Section 2: How It Works + Popular Themes */}
        <Box
          sx={{
            position: "relative",
            minHeight: "100vh",
            // background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
            py: 8,
            zIndex: 1,
          }}
        >
          <Container maxWidth="lg">
            {/* How It Works */}
            <Box sx={{ mb: 12 }}>
              <Typography
                variant="h3"
                component="h2"
                sx={{
                  textAlign: "center",
                  fontWeight: "bold",
                  color: "white",
                  mb: 6,
                  textShadow: "0 4px 8px rgba(0,0,0,0.3)",
                }}
              >
                How It Works
              </Typography>

              {/* Animated Bingo Balls */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  mb: 6,
                  gap: 2,
                }}
              >
                {["B", "I", "N", "G", "O"].map((letter, index) => (
                  <Box
                    key={letter}
                    sx={{
                      width: 60,
                      height: 60,
                      borderRadius: "50%",
                      backgroundColor: [
                        "#4F46E5",
                        "#059669",
                        "#DC2626",
                        "#F59E0B",
                        "#8B5CF6",
                      ][index],
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "1.8rem",
                      fontWeight: "bold",
                      animation: `${bounceAnimation} 2s infinite`,
                      animationDelay: `${index * 0.2}s`,
                      boxShadow: "0 8px 16px rgba(0,0,0,0.3)",
                    }}
                  >
                    {letter}
                  </Box>
                ))}
              </Box>

              <Grid container spacing={4}>
                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      background: "rgba(255, 255, 255, 0.1)",
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(255, 255, 255, 0.2)",
                      borderRadius: 3,
                      color: "white",
                      textAlign: "center",
                      p: 3,
                      transition: "all 0.3s ease",
                      animation: `${slideInAnimation} 1s ease-out`,
                      animationDelay: "0.2s",
                      animationFillMode: "backwards",
                      "&:hover": {
                        background: "rgba(255, 255, 255, 0.2)",
                        transform: "translateY(-5px)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 70,
                        height: 70,
                        borderRadius: "50%",
                        backgroundColor: "#4F46E5",
                        color: "white",
                        fontSize: "1.8rem",
                        fontWeight: "bold",
                        mb: 3,
                        animation: `${pulseAnimation} 3s infinite`,
                        position: "relative",
                        "&::before": {
                          content: '""',
                          position: "absolute",
                          top: -5,
                          left: -5,
                          right: -5,
                          bottom: -5,
                          borderRadius: "50%",
                          background:
                            "linear-gradient(45deg, #4F46E5, #8B5CF6)",
                          animation: `${rotateAnimation} 3s linear infinite`,
                          zIndex: -1,
                        },
                      }}
                    >
                      1
                    </Box>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
                      Share the Link
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Send one simple link to your team. No downloads, no apps,
                      no friction.
                    </Typography>

                    {/* Floating link icons */}
                    <Box
                      sx={{
                        position: "relative",
                        mt: 2,
                        height: 40,
                        overflow: "hidden",
                      }}
                    >
                      {[0, 1, 2].map((i) => (
                        <Box
                          key={i}
                          sx={{
                            position: "absolute",
                            animation: `${fadeInUpAnimation} 2s ease-out infinite`,
                            fontSize: "1.2rem",
                            left: `${20 + i * 30}%`,
                            animationDelay: `${i * 0.5}s`,
                          }}
                        >
                          🔗
                        </Box>
                      ))}
                    </Box>
                  </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      background: "rgba(255, 255, 255, 0.1)",
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(255, 255, 255, 0.2)",
                      borderRadius: 3,
                      color: "white",
                      textAlign: "center",
                      p: 3,
                      transition: "all 0.3s ease",
                      animation: `${slideInAnimation} 1s ease-out`,
                      animationDelay: "0.4s",
                      animationFillMode: "backwards",
                      "&:hover": {
                        background: "rgba(255, 255, 255, 0.2)",
                        transform: "translateY(-5px)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 70,
                        height: 70,
                        borderRadius: "50%",
                        backgroundColor: "#059669",
                        color: "white",
                        fontSize: "1.8rem",
                        fontWeight: "bold",
                        mb: 3,
                        animation: `${pulseAnimation} 3s infinite`,
                        animationDelay: "0.5s",
                      }}
                    >
                      2
                    </Box>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
                      Customize Your Theme
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Pick from our themes or create your own. Perfect for any
                      corporate event.
                    </Typography>

                    {/* Floating customization icons */}
                    <Box
                      sx={{
                        position: "relative",
                        mt: 2,
                        height: 40,
                        overflow: "hidden",
                      }}
                    >
                      {["🎨", "🎯", "✨"].map((icon, i) => (
                        <Box
                          key={i}
                          sx={{
                            position: "absolute",
                            animation: `${bounceAnimation} 2s infinite`,
                            fontSize: "1.2rem",
                            left: `${15 + i * 30}%`,
                            animationDelay: `${i * 0.3}s`,
                          }}
                        >
                          {icon}
                        </Box>
                      ))}
                    </Box>
                  </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Card
                    sx={{
                      background: "rgba(255, 255, 255, 0.1)",
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(255, 255, 255, 0.2)",
                      borderRadius: 3,
                      color: "white",
                      textAlign: "center",
                      p: 3,
                      transition: "all 0.3s ease",
                      animation: `${slideInAnimation} 1s ease-out`,
                      animationDelay: "0.6s",
                      animationFillMode: "backwards",
                      "&:hover": {
                        background: "rgba(255, 255, 255, 0.2)",
                        transform: "translateY(-5px)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 70,
                        height: 70,
                        borderRadius: "50%",
                        backgroundColor: "#DC2626",
                        color: "white",
                        fontSize: "1.8rem",
                        fontWeight: "bold",
                        mb: 3,
                        animation: `${pulseAnimation} 3s infinite`,
                        animationDelay: "1s",
                      }}
                    >
                      3
                    </Box>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
                      Play & Engage
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Watch your team connect and have fun, whether in-person or
                      virtual.
                    </Typography>

                    {/* Floating celebration icons */}
                    <Box
                      sx={{
                        position: "relative",
                        mt: 2,
                        height: 40,
                        overflow: "hidden",
                      }}
                    >
                      {["🎉", "🎊", "🏆", "🎯", "✨"].map((icon, i) => (
                        <Box
                          key={i}
                          sx={{
                            position: "absolute",
                            animation: `${fadeInUpAnimation} 1.5s ease-out infinite`,
                            fontSize: "1.2rem",
                            left: `${10 + i * 20}%`,
                            animationDelay: `${i * 0.2}s`,
                          }}
                        >
                          {icon}
                        </Box>
                      ))}
                    </Box>
                  </Card>
                </Grid>
              </Grid>
            </Box>

            {/* Popular Themes */}
            <Box>
              <Typography
                variant="h3"
                component="h3"
                sx={{
                  textAlign: "center",
                  fontWeight: "bold",
                  color: "white",
                  mb: 6,
                  textShadow: "0 4px 8px rgba(0,0,0,0.3)",
                }}
              >
                Popular Themes
              </Typography>
              <Box
                sx={{
                  position: "relative",
                  overflow: "hidden",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "80px",
                    height: "100%",
                    // background:
                    //   "linear-gradient(to right, rgba(118, 75, 162, 1), transparent)",
                    zIndex: 2,
                    pointerEvents: "none",
                  },
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    right: 0,
                    width: "50px",
                    height: "100%",
                    // background:
                    //   "linear-gradient(to left, rgba(118, 75, 162, 1), transparent)",
                    zIndex: 2,
                    pointerEvents: "none",
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    pb: 2,
                    pt: 2,
                    width: "200%", // Double width to accommodate duplicated items
                    animation: `${horizontalScrollAnimation} 20s linear infinite`,
                    "&:hover": {
                      animationPlayState: "paused", // Pause on hover
                    },
                  }}
                >
                  {/* First set of chips */}
                  {useCases.map((useCase, index) => (
                    <Chip
                      key={`first-${index}`}
                      label={useCase.label}
                      sx={{
                        backgroundColor: useCase.color,
                        color: "white",
                        fontSize: "1.1rem",
                        py: 2.5,
                        px: 3,
                        minWidth: "150px",
                        flexShrink: 0,
                        transition: "all 0.3s ease",
                        animation: `${fadeInUpAnimation} 1s ease-out`,
                        animationDelay: `${index * 0.1}s`,
                        animationFillMode: "backwards",
                        "&:hover": {
                          backgroundColor: useCase.color,
                          opacity: 0.8,
                          transform: "scale(1.1) translateY(-2px)",
                          // boxShadow: "0 8px 16px rgba(0,0,0,0.3)",
                        },
                      }}
                    />
                  ))}
                  {/* Duplicate set for seamless loop */}
                  {useCases.map((useCase, index) => (
                    <Chip
                      key={`second-${index}`}
                      label={useCase.label}
                      sx={{
                        backgroundColor: useCase.color,
                        color: "white",
                        fontSize: "1.1rem",
                        py: 2.5,
                        px: 3,
                        minWidth: "140px",
                        flexShrink: 0,
                        transition: "all 0.3s ease",
                        animation: `${fadeInUpAnimation} 1s ease-out`,
                        animationDelay: `${index * 0.1}s`,
                        animationFillMode: "backwards",
                        "&:hover": {
                          backgroundColor: useCase.color,
                          opacity: 0.8,
                          transform: "scale(1.1) translateY(-2px)",
                          boxShadow: "0 8px 16px rgba(0,0,0,0.3)",
                        },
                      }}
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          </Container>
        </Box>

        {/* Section 3: Perfect For Any Venue */}
        <Box
          sx={{
            position: "relative",
            minHeight: "100vh",
            // background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            py: 8,
            zIndex: 1,
          }}
        >
          <Container maxWidth="lg">
            <Typography
              variant="h3"
              component="h2"
              sx={{
                textAlign: "center",
                fontWeight: "bold",
                color: "white",
                mb: 8,
                textShadow: "0 4px 8px rgba(0,0,0,0.3)",
              }}
            >
              Perfect For Any Venue
            </Typography>

            <Grid container spacing={4} sx={{ mb: 8 }}>
              {venues.map((venue, index) => (
                <Grid
                  item
                  xs={6}
                  sm={6}
                  md={3}
                  key={index}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    // Ensure all cards have equal width on mobile
                    "& > *": {
                      width: "100%",
                      maxWidth: { xs: "200px", sm: "250px", md: "none" },
                    },
                  }}
                >
                  <Card
                    sx={{
                      background: "rgba(255, 255, 255, 0.1)",
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(255, 255, 255, 0.2)",
                      borderRadius: 3,
                      color: "white",
                      textAlign: "center",
                      p: 3,
                      transition: "all 0.3s ease",
                      animation: `${fadeInUpAnimation} 1s ease-out`,
                      animationDelay: `${index * 0.2}s`,
                      animationFillMode: "backwards",
                      width: "100%",
                      minHeight: { xs: "140px", sm: "160px", md: "auto" }, // Consistent height
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      "&:hover": {
                        background: "rgba(255, 255, 255, 0.2)",
                        transform: "translateY(-5px) scale(1.05)",
                        boxShadow: "0 12px 24px rgba(0,0,0,0.3)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                        mb: 2,
                        animation: `${bounceAnimation} 2s infinite`,
                        animationDelay: `${index * 0.1}s`,
                      }}
                    >
                      {venue.icon}
                    </Box>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: "bold",
                        fontSize: { xs: "1rem", sm: "1.1rem", md: "1.25rem" },
                      }}
                    >
                      {venue.label}
                    </Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* Additional Features */}
            <Grid container spacing={4} sx={{ mb: 8 }}>
              <Grid item xs={12} md={6}>
                <Card
                  sx={{
                    background: "rgba(255, 255, 255, 0.1)",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    borderRadius: 3,
                    color: "white",
                    p: 4,
                    height: "100%",
                    animation: `${glowPulse} 5s ease-in-out infinite`,
                    "&:hover": {
                      transform: "translateY(-3px)",
                    },
                  }}
                >
                  <Typography variant="h5" sx={{ fontWeight: "bold", mb: 3 }}>
                    🚀 Quick Setup
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ opacity: 0.9, lineHeight: 1.6 }}
                  >
                    Get your bingo game ready in under 2 minutes. No technical
                    skills required - just pick your theme and share the link.
                  </Typography>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card
                  sx={{
                    background: "rgba(255, 255, 255, 0.1)",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    borderRadius: 3,
                    color: "white",
                    p: 4,
                    height: "100%",
                    animation: `${glowPulse} 5s ease-in-out infinite`,
                    animationDelay: "0.5s",
                    "&:hover": {
                      transform: "translateY(-3px)",
                    },
                  }}
                >
                  <Typography variant="h5" sx={{ fontWeight: "bold", mb: 3 }}>
                    📊 Real-time Updates
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{ opacity: 0.9, lineHeight: 1.6 }}
                  >
                    Watch engagement happen live as your team marks off squares.
                    Perfect for keeping energy high during events.
                  </Typography>
                </Card>
              </Grid>
            </Grid>

            {/* CTA Section */}
            <Box sx={{ textAlign: "center" }}>
              <Button
                variant="contained"
                size="large"
                sx={{
                  backgroundColor: "white",
                  "&:hover": {
                    backgroundColor: "transparent",
                    transform: "translateY(-2px) scale(1.05)",
                    color: "white",
                  },
                  color: "black",
                  py: 3,
                  px: 6,
                  borderRadius: 3,
                  fontSize: "1.2rem",
                  fontWeight: "bold",
                  textTransform: "none",
                  boxShadow: "0 10px 25px rgba(79, 70, 229, 0.4)",
                  transition: "all 0.3s ease",
                  animation: `${pulseAnimation} 4s infinite`,
                }}
              >
                Start Your First Game
              </Button>
              <Typography
                variant="body1"
                sx={{
                  color: "rgba(255, 255, 255, 0.8)",
                  mt: 3,
                  fontSize: "1.1rem",
                }}
              >
                No credit card required • Setup in under 2 minutes
              </Typography>
            </Box>
          </Container>
        </Box>
      </Box>

      <Footer />
    </Box>
  );
}
